import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './quotationPreview.css';

const STORAGE_KEY = 'commercial-quotation-preview-payload';

function Letterhead() {
    return (
        <div className="letterhead">
            <img src="/templates/images/quotation-header.png" alt="Astern Technologies" className="letterhead-img" />
        </div>
    );
}

const fmt = n => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Map the payment method to the labels that appear on the totals rows.
// Cash → "Total Cash Price" / "Final Cash Price"
// 36M CC → "Total 36M CC Price" / "Final 36M CC Price"
// 60M CC → "Total 60M CC Price" / "Final 60M CC Price"
function paymentLabels(method) {
    if (method === 'cc36') return { name: '36M CC', subtitle: '36-month Credit Card Instalment' };
    if (method === 'cc60') return { name: '60M CC', subtitle: '60-month Credit Card Instalment' };
    return { name: 'Cash', subtitle: 'Cash Payment' };
}

// ─── Header used on both quotations ────────────────────────────────────────────
function AttnBlock({ p }) {
    return (
        <div className="attn-grid">
            <div className="label">Attn</div><div>{p.picName || ''}</div>
            <div className="label">Date</div><div>{p.date || ''}</div>
            <div className="label">Company</div><div>{p.companyName || ''}</div>
            <div className="label">Ref No.</div><div>{p.refNo || ''}</div>
            <div className="label">Address</div><div>{p.address || ''}</div>
            <div className="label">Tel No.</div><div>{p.picMobile || ''}</div>
            <div className="label">Validity</div><div>30 days</div>
            <div className="label">Email</div><div>{p.picEmail || ''}</div>
        </div>
    );
}

// ─── Payment Term (same content on both halves) ────────────────────────────────
function PaymentTerm({ withRemark }) {
    return (
        <div className="notes">
            <div className="section-title">3.0 PAYMENT TERM</div>
            <p>3.1 Our progressive payment term as below:</p>
            <ol className="payment-list" type="i">
                <li>10% upon acceptance and signing of quotation.</li>
                <li>50% upon technical drawing confirmation, before delivery.</li>
                <li>35% upon complete installation, before submission to TNB.</li>
                <li>5% upon complete TNB Commencement of Operation Date (COD).</li>
            </ol>
            {withRemark && (
                <p className="remark">
                    Remark: To ensure smooth project handover and system performance, we kindly request full payment upon installation completion. In cases of delayed payment, system operation may be temporarily paused, and further action may be considered if unresolved. We appreciate your support and understanding.
                </p>
            )}
        </div>
    );
}

// ─── Project Duration (same content on both halves) ────────────────────────────
function ProjectDuration() {
    return (
        <div className="notes">
            <div className="section-title">4.0 PROJECT DURATION</div>
            <p>4.1 The project workflow as below:</p>
            <ol className="duration-list" type="i">
                <li>Connection Assessment Study with TNB Approval: 4-6 weeks (for system size above 100kWp)</li>
                <li>Technical assessment: 4-6 weeks</li>
                <li>Stock preparation: Ex-stock or 4-6 weeks order</li>
                <li>Installation and configuration: 4-6 weeks, subject to weather constraints (300 – 600 kWp)</li>
                <li>ST License application and approval: 3 months (for system size above 100kWp)</li>
                <li>Change of TNB bi-directional meter: 2-4 weeks, subject to TNB arrangement</li>
                <li>Handover and system update: 1 week</li>
            </ol>
        </div>
    );
}

// ─── Signature block (pages 2 and 4) ──────────────────────────────────────────
function Signatures({ p }) {
    return (
        <div className="signature-grid">
            <div className="signature-block">
                <div className="caption">Prepared by:</div>
                <div className="line">
                    <div className="bold">ASTERN TECHNOLOGIES SDN. BHD.</div>
                    <div>Name: {p.agentName || ''}</div>
                    <div>Position: {p.agentPosition || ''}</div>
                    <div>Date: {p.date || ''}</div>
                </div>
            </div>
            <div className="signature-block">
                <div className="caption">Accepted by:</div>
                <div className="line">
                    <div className="bold">{p.companyName || ''}</div>
                    <div>Name: {p.picName || ''}</div>
                    <div>Position: {p.picPosition || ''}</div>
                    <div>Date: {p.date || ''}</div>
                </div>
            </div>
        </div>
    );
}

// ─── PAGE 1: Supply scope + 80% total + Notes 2.4 + Payment ────────────────────
function SupplyScope({ p }) {
    const program = p.program || 'ATAP';
    const kwp = Number(p.kwp || 0).toFixed(2);
    const panelQty = Number(p.panelQty || 0);
    const panelWatt = p.panelWatt || '640';
    const kwac = Number(p.kwac || 0);
    const part80 = fmt(p.commercialPart80);
    const labels = paymentLabels(p.commercialPaymentMethod);
    const battKwh = Number(p.batteryKwh || 0);
    const battQty = Number(p.batteryQty || 0);
    const hasBatt = battKwh > 0 && battQty > 0;
    const rows = [];
    let n = 1;

    rows.push({
        no: n++,
        title: `Supply Solar PV Module with a cumulative capacity of ${kwp}kWp`,
        desc: [
            '- Trina Solar Mono-crystalline half-cell with MyHijau mark or equivalent.',
            '- 15 years on product material and workmanship warranty from manufacturer.',
            '- 30 years linear output warranty from manufacturer.',
        ],
        qty: <>{panelQty} units<br/>({panelWatt}Wp)</>,
    });
    rows.push({
        no: n++,
        title: `Supply Solar Inverter with a cumulative capacity of ${kwac}kWac`,
        desc: [
            '- Solis / Goodwe high efficiency string inverter with MyHijau mark or equivalent.',
            '- 10 years product warranty.',
        ],
        qty: '1 set',
    });
    if (hasBatt) {
        // Show the per-module size when there are multiple modules (e.g. 64kWh = 4 ×
        // 16kWh) so "Qty 4 Units" reads correctly; single module shows its total kWh.
        const perUnitKwh = battQty > 1 ? Math.round(battKwh / battQty) : battKwh;
        rows.push({
            no: n++,
            title: 'Battery Storage System',
            desc: [`- Goodwe BAT ${perUnitKwh}kWh`, '- 10 years product and performance warranty.'],
            qty: `${battQty} ${battQty > 1 ? 'Units' : 'Unit'}`,
        });
    }
    rows.push({
        no: n++,
        title: 'Supply roof mounting structure',
        desc: ['- Tile / Tin roof mounting structure, complete with rail, clamp and connector.', '- 10 years product warranty.'],
        qty: '1 set',
    });
    rows.push({
        no: n++,
        title: 'Supply Balance of System / Accessories',
        desc: ['- Combiner box, breaker, accessories and all necessary wiring for power and communication.', '- 2 years product warranty.'],
        qty: '1 set',
    });

    return (
        <>
            <div className="program-line">
                SUBJECT: TO SUPPLY MATERIALS FOR {kwp} KWP {program} GRID CONNECTED SOLAR PV SYSTEM
            </div>
            <div className="section-title">1.0 SCOPE OF WORK</div>
            <table className="scope-table">
                <thead>
                    <tr><th className="no-col">No</th><th>Item</th><th className="qty-col">Qty</th></tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.no} className="avoid-break">
                            <td className="no-col">{r.no}</td>
                            <td>
                                <div className="item-title">{r.title}</div>
                                {r.desc.map((d, i) => <div key={i} className="item-desc">{d}</div>)}
                            </td>
                            <td className="qty-col">{r.qty}</td>
                        </tr>
                    ))}
                    <tr className="total-row"><td colSpan={3}>TOTAL {labels.name.toUpperCase()} PRICE: RM {part80}</td></tr>
                </tbody>
            </table>

            <div className="notes" style={{ marginTop: '4mm' }}>
                <div className="section-title">2.0 NOTES</div>
                <p>2.4 Product warranty will be void if the parts defects due to third party fault / vandalism / act of God / Grid Error.</p>
            </div>

            <PaymentTerm />
        </>
    );
}

// ─── PAGE 3: Install scope + 20% + SST + Grand Total + Notes 2.1 ───────────────
function InstallScope({ p }) {
    const program = p.program || 'ATAP';
    const kwp = Number(p.kwp || 0).toFixed(2);
    const kwac = Number(p.kwac || 0);
    const part20 = fmt(p.commercialPart20);
    const sst = fmt(p.commercialSst);
    const grand = fmt(p.commercialPart20 + p.commercialSst);
    const labels = paymentLabels(p.commercialPaymentMethod);

    const rows = [
        {
            no: 1,
            title: 'Construction and Installation of Solar PV Module and Solar Inverter',
            desc: [`- ${kwp} kWp Trina Solar PV Module`, `- ${kwac} kWac Solis / Goodwe Inverter`],
            qty: '1 set',
        },
        {
            no: 2,
            title: 'Install roof mounting structure',
            desc: ['- Tile / Tin roof mounting structure, complete with rail, clamp and connector.'],
            qty: '1 set',
        },
        {
            no: 3,
            title: 'Install Balance of System / Accessories',
            desc: ['- Combiner box, breaker, accessories and all necessary wiring for power and communication.'],
            qty: '1 set',
        },
        {
            no: 4,
            title: 'Testing & Commissioning (T&C)',
            desc: ['- System installation is in accordance to MS1837 of the Malaysian Standard Installation of Grid Connected Solar System.'],
            qty: '1 set',
        },
        {
            no: 5,
            title: 'Supply Technical Drawings and Authority Submission',
            desc: ['- Drawings, Technical Design, and SELCO or ATAP related application to SEDA and TNB.'],
            qty: '1 set',
        },
        {
            no: 6,
            title: 'Operation & Maintenance',
            desc: ['- Annual cleaning, technical support, online monitoring, preventive check, visual and thermal inspection of hot spots, and data log analysis.'],
            qty: p.operationWarrantyYears || '1 year',
        },
        {
            no: 7,
            title: 'Defect Liability Period',
            desc: ['- Labor warranty and roof water-leakage warranty from the date of successful commissioning.'],
            qty: p.defectWarrantyYears || '1 set',
        },
    ];

    return (
        <>
            <div className="program-line">
                SUBJECT: TO CONSTRUCT AND INSTALL {kwp} KWP {program} GRID CONNECTED SOLAR PV SYSTEM
            </div>
            <div className="section-title">1.0 SCOPE OF WORK</div>
            <table className="scope-table">
                <thead>
                    <tr><th className="no-col">No</th><th>Item</th><th className="qty-col">Qty</th></tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.no} className="avoid-break">
                            <td className="no-col">{r.no}</td>
                            <td>
                                <div className="item-title">{r.title}</div>
                                {r.desc.map((d, i) => <div key={i} className="item-desc">{d}</div>)}
                            </td>
                            <td className="qty-col">{r.qty}</td>
                        </tr>
                    ))}
                    <tr className="total-row"><td colSpan={3}>{labels.name.toUpperCase()} PRICE: RM {part20}</td></tr>
                    <tr className="total-row"><td colSpan={3}>SST 6%: RM {sst}</td></tr>
                    <tr className="total-row"><td colSpan={3}>FINAL {labels.name.toUpperCase()} PRICE (incl SST): RM {grand}</td></tr>
                </tbody>
            </table>

            <div className="notes" style={{ marginTop: '4mm' }}>
                <div className="section-title">2.0 NOTES</div>
                <p>2.1 Quotation does not include any modification to existing electrical network if required by TNB. PV System will be connected directly to house distribution box incoming.</p>
            </div>
        </>
    );
}

// ─── PAGE 4: Notes 2.2-2.7 + Payment (with remark) + Duration + Signatures ─────
function InstallTail({ p }) {
    return (
        <>
            <div className="notes">
                <p>2.2 Reinforcement of roof to cater for the additional load of the PV panels will be quoted separately if required.</p>
                <p>2.3 Final system size will depend on detail site survey and roof space measurement upon confirmation of order.</p>
                <p>2.4 Product warranty will be void if the parts defects due to third party fault / vandalism / act of God / Grid Error.</p>
                <p>2.5 Hacking and plastering is not included in this quotation. Trunking / PVC will be provided.</p>
                <p>2.6 Booking fee is non-refundable, except when SELCO / SEDA is not approved or roof installation is not possible after site visit.</p>
                <p>2.7 Wifi connection should be provided by the client.</p>
            </div>
            <PaymentTerm withRemark />
            <ProjectDuration />
            <Signatures p={p} />
        </>
    );
}

export default function CommercialQuotationPreview() {
    const [payload, setPayload] = useState(null);
    const [downloading, setDownloading] = useState(false);

    // Clean PDF generation via html2canvas + jsPDF — bypasses the browser's
    // print engine entirely so the URL / page-number footer never appears.
    const downloadPdf = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            // Wait for fonts so glyphs render correctly in the snapshot.
            if (document.fonts && document.fonts.ready) {
                try { await document.fonts.ready; } catch {}
            }
            const pages = Array.from(document.querySelectorAll('.quote-page'));
            if (!pages.length) throw new Error('No .quote-page elements to capture.');
            const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: false });
            for (let i = 0; i < pages.length; i++) {
                const canvas = await html2canvas(pages[i], {
                    scale: 2.5,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                    imageTimeout: 15000,
                });
                const img = canvas.toDataURL('image/jpeg', 0.98);
                if (i > 0) pdf.addPage();
                pdf.addImage(img, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
            }
            const safeCompany = (payload?.companyName || 'Quotation').replace(/[\/\\:*?"<>|]/g, '').trim();
            const methodLabel = paymentLabels(payload?.commercialPaymentMethod).name;
            pdf.save(`${safeCompany} - Commercial Quotation (${methodLabel}).pdf`);
        } catch (e) {
            console.error(e);
            alert(`PDF generation failed: ${e.message || e}`);
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        try {
            setPayload(JSON.parse(raw));
            sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            console.error('Failed to parse commercial quotation payload', e);
        }
    }, []);

    useEffect(() => {
        if (payload?.companyName) {
            const methodLabel = paymentLabels(payload.commercialPaymentMethod).name;
            document.title = `Commercial Quotation (${methodLabel}) - ${payload.companyName}`;
        }
    }, [payload]);

    if (!payload) {
        return (
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
                <Typography variant="h6">No commercial quotation data found</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#64748B', mt: 1 }}>
                    Open this from the Form Generator's "Quotation" button in the Commercial tab.
                </Typography>
            </Box>
        );
    }

    return (
        <Box className="quote-root">
            <Box className="no-print quote-toolbar">
                <Button onClick={() => window.close()} variant="text" size="small">Close</Button>
                <Button onClick={() => window.print()} variant="outlined" size="small">
                    Print
                </Button>
                <Button onClick={downloadPdf} variant="contained" size="small" disabled={downloading}>
                    {downloading ? 'Generating…' : 'Download PDF'}
                </Button>
            </Box>
            <CommercialQuotationDocument payload={payload} />
        </Box>
    );
}

// Reusable document body (the 4 A4 .quote-page divs) — shared by the preview
// page above AND the Form Generator's direct client-side PDF (iPhone-safe, no tab).
export function CommercialQuotationDocument({ payload }) {
    if (!payload) return null;
    return (
        <>
            {/* PAGE 1 — Supply quotation: scope + 80% total + Notes 2.4 + Payment */}
            <div className="quote-page">
                <Letterhead />
                <div className="quote-title">QUOTATION</div>
                <AttnBlock p={payload} />
                <SupplyScope p={payload} />
            </div>

            {/* PAGE 2 — Project Duration + Signatures for the 80% portion */}
            <div className="quote-page">
                <Letterhead />
                <ProjectDuration />
                <Signatures p={payload} />
            </div>

            {/* PAGE 3 — Install quotation: scope + 20% + SST + Grand Total + Notes 2.1 */}
            <div className="quote-page">
                <Letterhead />
                <div className="quote-title">QUOTATION</div>
                <AttnBlock p={payload} />
                <InstallScope p={payload} />
            </div>

            {/* PAGE 4 — Notes 2.2-2.7 + Payment+Remark + Duration + Signatures (20%) */}
            <div className="quote-page">
                <Letterhead />
                <InstallTail p={payload} />
            </div>
        </>
    );
}
